import FBPostCRUDActions from '@mm/state/actions/FBPostCRUDActions';
import FBPostModel from '@mm/state/model/FBPostModel';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import proRibbonVertical from 'assets/operator-ribbon-vertical.svg';
import classnames from 'classnames';
import moment from 'moment';
import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { UncontrolledTooltip } from 'reactstrap';
import { Dispatch } from 'redux';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../../common/components/Grid/Grid';
import customersLogo from 'assets/logo-customers-blue.svg';
import { useCreateStartBotPromoterByType } from '../../use-create-start-bot-promoter-by-type';
import './StartBotRecommendations.pcss';

const useRedux = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const { facebookPage, recommended, freeOrSpecialPricePlan } = useSelector(
    (state: StoreState) => ({
      facebookPage: activeFacebookPageSelector(state),
      recommended: state.fbPost.recommended,
      freeOrSpecialPricePlan:
        activeFacebookPagePlanInfoSelector(state).info.isFreeOrSpecialPlan,
    }),
    shallowEqual,
  );

  const retrieveRecommended = async () =>
    facebookPage &&
    dispatch(FBPostCRUDActions.retrieveRecommended(facebookPage.id));

  return {
    retrieveRecommended,
    recommended,
    freeOrSpecialPricePlan,
  };
};

const CreatedAtCell = ({ createdAt }: { createdAt?: string }) => {
  let date = '';
  let time = '';

  if (createdAt) {
    const parsed = moment(createdAt);

    date = parsed.format('MM/DD/YYYY');
    time = parsed.format('HH:MM a');
  }

  return (
    <div>
      <div>{date}</div>
      <div className="start-bot-recommendations__grid-cell--time">{time}</div>
    </div>
  );
};

const PostCell = ({
  picture,
  message,
  permalinkUrl,
}: {
  picture?: string;
  message?: string;
  permalinkUrl?: string;
}) => (
  <div className="start-bot-recommendations__grid-cell--message">
    <img
      className="start-bot-recommendations__grid-cell--image"
      src={picture || customersLogo}
      alt=""
    />
    {message && (
      <a
        className="start-bot-recommendations__grid-cell--link"
        href={permalinkUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {message}
      </a>
    )}
  </div>
);

const ActionCell = ({
  post,
  freeOrSpecialPricePlan,
}: {
  post: FBPostModel;
  freeOrSpecialPricePlan: boolean;
}) => {
  const { promotableId } = post;
  const postId = promotableId && promotableId.split('_')[1];
  const createCommentGuard =
    useCreateStartBotPromoterByType('fb_comment_guard');
  const { november2021Pricing } = useSelector(combinedFeatureFlagsSelector);

  return (
    <div
      className={classnames('start-bot-recommendations__grid-cell--action', {
        'start-bot-recommendations__grid-cell--action-free':
          freeOrSpecialPricePlan,
      })}
      id={`recommended-post-id-${postId}`}
      onClick={() =>
        !freeOrSpecialPricePlan && createCommentGuard('', postId, post)
      }
    >
      {freeOrSpecialPricePlan && (
        <UncontrolledTooltip
          placement="top"
          target={`recommended-post-id-${postId}`}
        >
          Upgrade to {november2021Pricing ? 'Startup' : 'Operator'} to tailor
          Comment Guard content to each post!
        </UncontrolledTooltip>
      )}
      GO
    </div>
  );
};

const StartBotRecommendations = () => {
  const { recommended, retrieveRecommended, freeOrSpecialPricePlan } =
    useRedux();
  const gridTemplateColumns = `15% 50% 12% 12% 11%`;
  const { november2021Pricing } = useSelector(combinedFeatureFlagsSelector);

  useEffect(() => {
    retrieveRecommended();
  }, []);

  if (!recommended || recommended.length < 1) return null;
  return (
    <div className="start-bot-recommendations">
      <div className="start-bot-recommendations__title">
        Recommended for you
      </div>
      <div className="start-bot-recommendations__body">
        These posts would perform better if they had unique comment guards that
        worked with their content.
      </div>
      <Grid className="start-bot-recommendations__grid">
        <GridLabels
          className="start-bot-recommendations__grid-labels"
          gridTemplateColumns={gridTemplateColumns}
        >
          <GridCell className="start-bot-recommendations__grid-cell">
            Published
          </GridCell>
          <GridCell className="start-bot-recommendations__grid-cell">
            Post
          </GridCell>
          <GridCell className="start-bot-recommendations__grid-cell">
            Comments
          </GridCell>
          <GridCell className="start-bot-recommendations__grid-cell">
            New Leads
          </GridCell>
        </GridLabels>

        {recommended.map((post: FBPostModel) => (
          <GridRow
            className="start-bot-recommendations__grid-row"
            gridTemplateColumns={gridTemplateColumns}
          >
            <img
              className="start-bot-recommendations__grid-row--pro-ribbon"
              alt={`${november2021Pricing ? 'Startup' : 'Operator'} Feature`}
              src={proRibbonVertical}
            />
            <GridCell className="start-bot-recommendations__grid-cell">
              <CreatedAtCell createdAt={post.createdAt} />
            </GridCell>
            <GridCell className="start-bot-recommendations__grid-cell">
              <PostCell
                message={post.message}
                picture={post.picture}
                permalinkUrl={post.permalinkUrl}
              />
            </GridCell>
            <GridCell className="start-bot-recommendations__grid-cell">
              <span className="start-bot-recommendations__grid-cell--comments">
                {post.commentsCount}
              </span>
            </GridCell>
            <GridCell className="start-bot-recommendations__grid-cell">
              <span className="start-bot-recommendations__grid-cell--leads">
                {post.leadsCount}
              </span>
            </GridCell>
            <GridCell className="start-bot-recommendations__grid-cell">
              <ActionCell
                post={post}
                freeOrSpecialPricePlan={!!freeOrSpecialPricePlan}
              />
            </GridCell>
          </GridRow>
        ))}
      </Grid>
    </div>
  );
};

export default StartBotRecommendations;
